import { useSaleItemStore } from "@/store/sale-items.store"
import { useRentalIntakeItemStore } from "@/store/rental-intake-item.store"
import { useRentalDispenseItemStore } from "@/store/rental-dispense-item.store"
import { useAppStore } from "@/store/app.store"
import { type Ref } from "vue"
import { storeToRefs } from "pinia"
import { useLockStore } from "@/store/lock-store"
import { useAuthStore } from "@/store/auth.store.ts"
import { useWorkPeriodTemplateStore } from "@/store/work-period-templates.store.ts"
import { useInstructionFeedbackActivityTemplateStore } from "@/store/instruction-feedback-activity-templates.store.ts"
import { useWorkspaceStore } from "@/workspace/store/workspace.store"
import { HolidayController } from "@/controllers/model-controllers/holiday-controller"
import { KnownBusinessEntityContactController } from "@/controllers/model-controllers/known-business-entity-contact-controller"
import { ProjectController } from "@/controllers/model-controllers/project-controller"
import type { PersonController } from "@/controllers/model-controllers/person-controller"
import { MachineController } from "@/controllers/model-controllers/machine-controller"
import { KnownBusinessEntityController } from "@/controllers/model-controllers/known-business-entity-controller.ts"
import type { LocationController } from "@/controllers/model-controllers/location-controller"
import { PurchaseItemController } from "@/controllers/model-controllers/purchase-item-controller"
import type { SaleItemController } from "@/controllers/model-controllers/sale-item-controller"
import type { RentalIntakeItemController } from "@/controllers/model-controllers/rental-intake-item-controller"
import type { RentalDispenseItemController } from "@/controllers/rental-dispense-item-controller.ts"
import type { ProductController } from "@/controllers/model-controllers/product-controller"
import type { AppProductController } from "@/controllers/model-controllers/app-product-controller"
import type OrderController from "@/controllers/order-controller.ts"
import type { AvailabilityReasonController } from "@/controllers/model-controllers/availability-reason-controller.ts"
import type { WorkPeriodTemplateController } from "@/controllers/model-controllers/work-period-template-controller.ts"
import type { InstructionFeedbackActivityTemplateController } from "@/controllers/model-controllers/instruction-feedback/instruction-feedback-activity-template-controller.ts"
import type { WorkspaceNoteController } from "@/workspace/controllers/model-controllers/workspace-note-controller"
import type { WorkspaceAudienceGroupController } from "@/workspace/controllers/model-controllers/workspace-audience-group-controller"

type InitialDataSubControllers = {
  holidayController: HolidayController
  personController: PersonController
  knownBusinessEntityContactController: KnownBusinessEntityContactController
  projectController: ProjectController
  machineController: MachineController
  knownBusinessEntityController: KnownBusinessEntityController
  locationController: LocationController
  purchaseItemController: PurchaseItemController
  saleItemController: SaleItemController
  rentalIntakeItemController: RentalIntakeItemController
  rentalDispenseItemController: RentalDispenseItemController
  productController: ProductController
  appProductController: AppProductController
  orderController: OrderController
  availabilityReasonController: AvailabilityReasonController
  workPeriodTemplateController: WorkPeriodTemplateController
  instructionFeedbackActivityTemplateController: InstructionFeedbackActivityTemplateController
  workspaceNoteController: WorkspaceNoteController
  workspaceAudienceGroupController: WorkspaceAudienceGroupController
}

export class InitialDataController {
  private appStore = useAppStore()
  private authStore = useAuthStore()
  private workPeriodTemplateStore = useWorkPeriodTemplateStore()
  private instructionFeedbackActivityTemplateStore = useInstructionFeedbackActivityTemplateStore()
  private workspaceStore = useWorkspaceStore()
  private saleItemStore = useSaleItemStore()
  private rentalIntakeItemStore = useRentalIntakeItemStore()
  private rentalDispenseItemStore = useRentalDispenseItemStore()
  private instructionLockStore = useLockStore()

  private subControllers: InitialDataSubControllers
  private ordersOrInstructionsLoading: Promise<void> | null = null

  constructor(subControllers: InitialDataSubControllers) {
    this.subControllers = subControllers
  }

  public onOrdersOrInstructionsColumnMounted(): Promise<void> {
    // An initiated fetch is not necessarily complete. Every mounting column must
    // await the same initialization, rather than skipping requests already started.
    if (!this.ordersOrInstructionsLoading) {
      this.ordersOrInstructionsLoading = this.initializeOrdersOrInstructions().finally(() => {
        this.ordersOrInstructionsLoading = null
      })
    }
    return this.ordersOrInstructionsLoading
  }

  private async initializeOrdersOrInstructions() {
    const { initialOrdersFetchInitiated } = storeToRefs(this.appStore)
    await Promise.allSettled([
      this.subControllers.orderController.fetchInitialOrders(
        this.shouldFetch(initialOrdersFetchInitiated)
      ),
      this.retrieveTransactionItemsAndRentalItems(),
      this.retrieveBaseCollections()
    ])
    this.instructionLockStore.fetchLocks() // can be done after orders are fetched
  }

  public async onMapMounted() {
    await this.retrieveTransactionItemsAndRentalItems()
  }

  public async onInstructionFinderMounted() {
    await this.retrieveBaseCollections()
  }

  public async onAppMounted() {
    await Promise.allSettled([
      this.subControllers.appProductController.getList(),
      this.retrieveWorkPeriodTemplates(),
      this.retrieveInstructionFeedbackActivityTemplates()
    ])
  }

  private async retrieveInstructionFeedbackActivityTemplates() {
    const { activeUser } = storeToRefs(this.authStore)
    const kbec = activeUser.value?.knownBusinessEntityContact
    if (!kbec) {
      return
    }

    const { instructionFeedbackActivityTemplatesFetched } = storeToRefs(
      this.instructionFeedbackActivityTemplateStore
    )
    if (this.shouldFetch(instructionFeedbackActivityTemplatesFetched)) {
      await this.subControllers.instructionFeedbackActivityTemplateController.getListForKnownBusinessEntityContact(
        kbec
      )
    }
  }

  private async retrieveWorkPeriodTemplates() {
    const { activeUser } = storeToRefs(this.authStore)
    const kbec = activeUser.value?.knownBusinessEntityContact
    if (!kbec) {
      return
    }

    const { workPeriodTemplatesFetched } = storeToRefs(this.workPeriodTemplateStore)
    if (this.shouldFetch(workPeriodTemplatesFetched)) {
      await this.subControllers.workPeriodTemplateController.getListForKnownBusinessEntityContact(
        kbec
      )
    }
  }

  public async onIncomingInvoiceColumnMounted() {
    return Promise.all([
      this.retrieveBaseCollections(),
      this.retrieveTransactionItemsAndRentalItems()
    ])
  }

  public async onCustomerPurchaseOrderColumnMounted() {
    await this.retrieveBaseCollections()
  }

  public async onProductionStatementColumnMounted() {
    return Promise.all([
      this.retrieveBaseCollections(),
      this.retrieveTransactionItemsAndRentalItems()
    ])
  }

  public async onQuoteDataColumnMounted() {
    await Promise.all([
      this.retrieveBaseCollections(),
      this.retrieveTransactionItemsAndRentalItems()
    ])
  }

  /** The Workspace needs the base collections that hold the planner contacts, next to its own data. */
  public async onWorkspaceColumnMounted(): Promise<void> {
    await Promise.allSettled([this.retrieveBaseCollections(), this.retrieveWorkspaceCollections()])
  }

  private async retrieveWorkspaceCollections() {
    const { workspaceFetched, workspaceLoading } = storeToRefs(this.workspaceStore)
    if (!this.shouldFetch(workspaceFetched)) {
      return
    }

    workspaceLoading.value = true
    try {
      // The server only hands out the notes the user may see. Archived groups are included because
      // existing notes can still be shared with them.
      await Promise.allSettled([
        this.subControllers.workspaceAudienceGroupController.getList(),
        this.subControllers.workspaceNoteController.getList()
      ])
    } finally {
      workspaceLoading.value = false
    }
  }

  private async retrieveTransactionItemsAndRentalItems() {
    const { initialTransactionsAndMutationsFetchInitiated } = storeToRefs(this.appStore)
    if (this.shouldFetch(initialTransactionsAndMutationsFetchInitiated)) {
      await Promise.allSettled([
        this.subControllers.purchaseItemController.getList(),
        this.saleItemStore.fetchSaleItems(),
        this.rentalIntakeItemStore.fetchRentalIntakeItems(),
        this.rentalDispenseItemStore.fetchRentalDispenseItems()
      ])
    }
  }

  private async retrieveBaseCollections() {
    const { initialBaseCollectionsFetchInitiated } = storeToRefs(this.appStore)
    if (this.shouldFetch(initialBaseCollectionsFetchInitiated)) {
      await Promise.allSettled([
        this.subControllers.personController.getList(),
        this.subControllers.knownBusinessEntityContactController.getList(),
        this.subControllers.machineController.getList(),
        this.subControllers.projectController.getList(),
        this.subControllers.holidayController.getList(),
        this.subControllers.knownBusinessEntityController.getList(),
        this.subControllers.locationController.getTenantBaseLocationsList(), // Tenant-base locations only
        this.subControllers.productController.getList(),
        this.subControllers.availabilityReasonController.getList()
      ])
    }
  }

  private shouldFetch(initiatedFlag: Ref<boolean>) {
    if (!initiatedFlag.value) {
      initiatedFlag.value = true
      return true
    }
    return false
  }
}
